import { useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft } from "lucide-react";
import { Logo, Panel, Btn, Field, Input } from "@/components/kit";
import { useAuth, homeForRole } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";

export default function Login() {
  const [params] = useSearchParams();
  const portal = params.get("portal");
  const isGuestPortal = portal === "guest";
  const [mode, setMode] = useState(isGuestPortal ? "register" : "login");
  const [form, setForm] = useState({ email: "", password: "", name: "", phone: "" });
  const [busy, setBusy] = useState(false);
  const { login, register } = useAuth();
  const navigate = useNavigate();

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const user = mode === "login"
        ? await login(form.email, form.password)
        : await register({ email: form.email, password: form.password, name: form.name, phone: form.phone });
      toast.success(`Welcome, ${user.name}`);
      navigate(user.role === "superadmin" ? "/platform" : user.role === "guest" && mode === "register" ? "/customer" : "/portals");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="bd-page flex flex-col">
      <header className="sticky top-0 z-30 border-b border-[#2E1256] bg-[#0B031E]/80 backdrop-blur-xl">
        <div className="max-w-7xl mx-auto flex items-center justify-between px-6 py-4">
          <Link to="/" data-testid="header-logo"><Logo /></Link>
          <button data-testid="back-home" onClick={() => navigate("/")} className="inline-flex items-center gap-1.5 text-sm font-semibold text-purple-200 hover:text-white transition-colors">
            <ArrowLeft size={16} /> Back to home
          </button>
        </div>
      </header>
      <div className="flex-1 flex flex-col items-center justify-center px-6 py-10">
        <Panel className="w-full max-w-md p-8 bd-fade-up">
        <h1 className="font-head text-2xl font-bold text-white mb-1">
          {mode === "login" ? "Sign in to BeetDrop" : "Create your guest account"}
        </h1>
        <p className="text-sm text-purple-300/70 mb-6">
          {mode === "login" ? "Access your portal" : "Book services and track your requests"}
        </p>
        <form onSubmit={submit} className="space-y-4">
          {mode === "register" && (
            <>
              <Field label="Full name"><Input data-testid="reg-name" required value={form.name} onChange={set("name")} placeholder="Grace Guest" /></Field>
              <Field label="Phone (optional)"><Input data-testid="reg-phone" value={form.phone} onChange={set("phone")} placeholder="+1 555 0100" /></Field>
            </>
          )}
          <Field label="Email"><Input data-testid="auth-email" type="email" required value={form.email} onChange={set("email")} placeholder="you@example.com" /></Field>
          <Field label="Password"><Input data-testid="auth-password" type="password" required value={form.password} onChange={set("password")} placeholder="••••••••" /></Field>
          <Btn type="submit" className="w-full" disabled={busy} data-testid="auth-submit">
            {busy ? "Please wait…" : mode === "login" ? "Sign in" : "Create account"}
          </Btn>
        </form>
        <div className="mt-5 text-center text-sm text-purple-300/70">
          {mode === "login" ? (
            <>New guest? <button data-testid="switch-register" className="font-semibold text-[#FF6B7D]" onClick={() => setMode("register")}>Create an account</button></>
          ) : (
            <>Already have an account? <button data-testid="switch-login" className="font-semibold text-[#FF6B7D]" onClick={() => setMode("login")}>Sign in</button></>
          )}
        </div>
        <div className="mt-3 pt-3 border-t border-[#2E1256] text-center text-sm text-purple-300/70">
          Run a hospitality business? <Link to="/business/register" data-testid="link-business-register" className="font-semibold text-[#FF6B7D]">Register your business</Link>
        </div>
      </Panel>
      </div>
    </div>
  );
}
